import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useResetRecoilState } from "recoil";
import {
  startSubscription,
  updateBilling,
  updateStripeCustomer,
  getStripeLink,
} from "../../../../../api/Billing";
import { billingAtom, newBillingAtom } from "../../../../../atoms/BillingAtom";
import { MOBILE_WIDTH, TABLET_WIDTH } from "../../../../../utils/HelperStyles";
import StyledButton from "../../../../StyledButton";
import ActivateLeft from "./ActivateLeft";
import CreditCardComponent from "./CreditCardComponent";
import { InputPayment, PaymentFieldName } from "./PaymentModalStyles";

interface IProps {
  onOkClicked: () => void;
  onCancelClicked: () => void;
  isModal?: boolean;
  customTitle?: string;
}

const ActivatePaymentContainer = styled.div<{ isModal?: boolean }>`
  display: flex;

  padding-top: 5vh;

  ${(props) =>
    props.isModal
      ? `
    background-color: white;
    padding-top: 2vh;
  `
      : ""}

  height: ${(props) => (props.isModal ? "100%" : "600px")};
  letter-spacing: 0px;
  width: 1000px;

  @media (max-width: ${TABLET_WIDTH}) {
    width: 900px;
  }

  @media (max-width: 900px) {
    width: 700px;
  }

  @media (max-width: ${MOBILE_WIDTH}) {
    flex-direction: column;
    align-items: center;
    overflow-y: scroll;
    height: 100vh;
    width: 100vw;
    margin-top: 20px;
  }
`;

export default function UpdatePayment(props: IProps): JSX.Element {
  const { onOkClicked, onCancelClicked, isModal, customTitle } = props;

  const resetBilling = useResetRecoilState(newBillingAtom);

  const [loading, setLoading] = useState<boolean>(false);
  const [billing, setBilling] = useRecoilState(newBillingAtom);
  const [oldBilling, setOldBilling] = useRecoilState(billingAtom);
  const [remainingDays, setRemainingDays] = useState<number>();

  const [gettingStripeLink, setGettingStripeLink] = useState(false);

  async function openStripeTab() {
    if (gettingStripeLink) return;
    setGettingStripeLink(true);
    const link = await getStripeLink();

    window.open(link);
    setGettingStripeLink(false);
  }

  async function handleSubmit() {
    setLoading(true);

    /**
     * These tons of comments are for future implementation
     */

    try {
      if (billing.vatId !== undefined || billing.companyName !== undefined) {
        const customerIDResponse = await updateBilling({
          vatId: billing.vatId,
          companyName: billing.companyName,
        });
        if (!customerIDResponse.success) {
          toast(customerIDResponse.message, { type: "error" });
          setLoading(false);
          return;
        }
      }

      await startSubscription({
        externalUsers: billing.externalUsers,
        isUpdate: true,
        admins: billing.admins,
        tier: billing.tier,
      });
      setLoading(false);
      setOldBilling((oldBil) => ({
        ...oldBil,
        update: true,
      }));

      if (billing.activeUser && billing.subscriptionId) {
        onCancelClicked();
      } else {
        onOkClicked();
      }
    } catch (err) {
      setLoading(false);
      toast(err.message, {
        type: "error",
      });
      // toastNotification({
      //   text: err.message,
      //   type: "error",
      //   icon: (
      //     <IconSocialLair name="report" color="#fff" width={20} height={20} />
      //   ),
      // });
      onCancelClicked();
    }

    resetBilling();
  }

  function checkBilling() {
    let valid = true;

    Object.entries(billing.billingAddress).map((item) => {
      const [key, value] = item;
      if (!value) {
        setBilling((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            billingAddress: {
              ...prevState.errors.billingAddress,
              [key]: true,
            },
          },
        }));
        valid = false;
      } else {
        setBilling((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            billingAddress: {
              ...prevState.errors.billingAddress,
              [key]: false,
            },
          },
        }));
      }
    });
    return valid;
  }

  function checkShipping() {
    let valid = true;

    Object.entries(billing.shippingAddress).map((item) => {
      const [key, value] = item;
      if (!value) {
        setBilling((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            shippingAddress: {
              ...prevState.errors.shippingAddress,
              [key]: true,
            },
          },
        }));
        valid = false;
      } else {
        setBilling((prevState) => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            shippingAddress: {
              ...prevState.errors.shippingAddress,
              [key]: false,
            },
          },
        }));
      }
    });
    return valid;
  }

  function checkForm() {
    // Check billing address
    let valid = true;
    let newErrors = { ...billing.errors };

    valid = checkBilling();

    if (billing.customShipping) {
      valid = checkShipping() && valid;
    }

    if (billing.name === "" || !billing.name) {
      valid = false;
      newErrors.name = true;
    } else {
      newErrors.name = false;
    }

    if (!billing.termsAndConditions) {
      valid = false;
    }

    setBilling((prevState) => ({
      ...prevState,
      errors: newErrors,
    }));

    return valid;
  }

  useEffect(() => {
    if (billing.subscriptionEndDate) {
      const date1 = dayjs(Date.now());
      const date2 = dayjs(billing.subscriptionEndDate * 1000);

      setRemainingDays(date2.diff(date1, "day"));
    }
  }, [billing]);

  return (
    <ActivatePaymentContainer isModal={isModal}>
      <ActivateLeft customTitle={customTitle} />

      <div style={{ width: "100%", padding: "0px 40px", minHeight: "100%" }}>
        <div>
          <div
            style={{
              fontSize: "22px",
              marginBottom: "20px",
              fontWeight: "bold",
            }}
          >
            Pay with your saved card{" "}
            <span
              onClick={openStripeTab}
              style={{
                color: "#fb972e",
                cursor: "pointer",
                opacity: gettingStripeLink ? 0.3 : 1,
              }}
            >
              &nbsp;(Update)
            </span>
          </div>
          <CreditCardComponent />
          <br />
          <PaymentFieldName style={{ marginBottom: "0px", paddingBottom: 0 }}>
            Extra Information (Optional)
          </PaymentFieldName>
          <PaymentFieldName
            style={{ marginBottom: "0px", paddingBottom: 0, fontSize: 14 }}
          >
            VAT ID:
          </PaymentFieldName>
          <InputPayment
            onChange={(event) => {
              event.preventDefault();
              setBilling((prevState) => ({
                ...prevState,
                vatId: event.target.value,
              }));
            }}
            placeholder="VAT ID"
            value={billing.vatId || ""}
            error={false}
          />
          <PaymentFieldName
            style={{ marginBottom: "0px", paddingBottom: 0, fontSize: 14 }}
          >
            Company name:
          </PaymentFieldName>
          <InputPayment
            onChange={(event) => {
              event.preventDefault();
              setBilling((prevState) => ({
                ...prevState,
                companyName: event.target.value,
              }));
            }}
            placeholder="Enter your company name"
            value={billing.companyName || ""}
            error={false}
          />
          <br />
          <br />
          <b>Please note: </b>
          <br /> <br />
          You will be charged a prorated amount today based on the remaining
          {remainingDays && <b>{" " + remainingDays + " "}</b>}
          day{(remainingDays || 2) > 1 ? "s" : null} in your subscription and
          your monthly renewal will increase by{" "}
          <b>
            $
            {(billing.admins - billing.initialAdmins) * billing.adminPrice +
              (billing.externalUsers - billing.initialExternalAdmins) *
                billing.externalUserPrice || "0"}
          </b>
          &nbsp;dollars.
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "20px",
            alignItems: "center",
          }}
        >
          <StyledButton
            isLoading={loading || !billing.card}
            disabled={loading || !billing.card}
            onClick={handleSubmit}
          >
            Submit
          </StyledButton>
        </div>
      </div>
    </ActivatePaymentContainer>
  );
}
